import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"; 
import { CityState } from "../models/state/cityState";
import { CityRequest } from "../models/request/cityRequest";
import { CityModel } from "../models/models/cityModel";
import { CityResource } from "../models/resource/cityResource";
import { cityService } from "../services/cityService";
import { CityResponse } from "../models/response/cityResponse";

const initialState: CityState = {
  cities: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
  citiesGrid: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
  city: {
    token: null,
    status: "idle",
    isLoading: false,
    error: null,
    message: "",
    blobData: null,
    data: null,
  },
};

export const getCities = createAsyncThunk<
    any, 
    { id: number },
  { rejectValue: string }
  >('cities/getCities', async ({id}, { rejectWithValue }) => {
  try {
    const response = await cityService.getCities(id);
    return response;
  } catch (error: any) {
    return rejectWithValue('An unexpected error occurred');
  }
});  

export const getCityById = createAsyncThunk<
  CityModel,
  { id: number },
  { rejectValue: string }
>("city/getCityById", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await cityService.getCityById(id);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const deleteCity = createAsyncThunk<
CityResponse,
  { data: number },
  { rejectValue: string }
>(
  "offerIssuer/deleteCity",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response =
        await cityService.deleteCity(data);
      return response;
    } catch (error: any) {
      return rejectWithValue("An unexpected error occurred");
    }
  }
);  


export const saveCity = createAsyncThunk<
any,
  { data: CityResource },
  { rejectValue: string }
>("city/saveCity", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await cityService.saveCity(data);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
});

export const getCitiesList = createAsyncThunk<
CityResponse,
  { data: CityRequest },
  { rejectValue: string }
>("city/getCitiesList", async ({ data }, { rejectWithValue }) => {
  try {
    const response = await cityService.getCitiesList(data);
    return response;
  } catch (error: any) {
    return rejectWithValue("An unexpected error occurred");
  }
}); 

const cityReducer = createSlice({
  name: "city",
  initialState,
  reducers: {
    clearBlobData: (state) => {
    state.citiesGrid.blobData = null;
  }},
  extraReducers: (builder) => {
    builder
      .addCase(getCities.pending, (state) => {
        state.cities.status = "loading";
        state.cities.isLoading = true;
      })
      .addCase(getCities.fulfilled, (state, action) => {
        state.cities.status = "idle";
        state.cities.data = action.payload;
        state.cities.isLoading = false;
      })
      .addCase(getCities.rejected, (state, action) => {
        state.cities.status = "failed";
        state.cities.isLoading = false;
        if (action.payload) {
          state.cities.error = action.payload;
          state.cities.message = action.payload;
        } else {
          state.cities.message = "An unexpected error occurred";
        }
      }) 
      .addCase(saveCity.pending, (state) => {
        state.cities.status = "loading";
        state.cities.isLoading = true;
      })
      .addCase(saveCity.fulfilled, (state, action) => {
        state.cities.status = "idle";
        state.cities.data = action.payload;
        state.cities.isLoading = false;
      })
      .addCase(saveCity.rejected, (state, action) => {
        state.cities.status = "failed";
        state.cities.isLoading = false;
        if (action.payload) {
          state.cities.error = action.payload;
          state.cities.message = action.payload;
        } else {
          state.cities.message = "An unexpected error occurred";
        }
      })
      .addCase(getCityById.pending, (state) => {
        state.city.status = "loading";
        state.city.isLoading = true;
      })
      .addCase(getCityById.fulfilled, (state, action) => {
        state.city.status = "idle";
        state.city.data = action.payload;
        state.city.isLoading = false;
      })
      .addCase(getCityById.rejected, (state, action) => {
        state.city.status = "failed";
        state.city.isLoading = false;
        if (action.payload) {
          state.city.error = action.payload;
          state.city.message = action.payload;
        } else {
          state.city.message = "An unexpected error occurred";
        }
      })
      .addCase(getCitiesList.pending, (state) => {
        state.citiesGrid.status = "loading";
        state.citiesGrid.isLoading = true;
      })
      .addCase(getCitiesList.fulfilled, (state, action) => {
        state.citiesGrid.status = "idle";
        state.citiesGrid.data = action.payload;
        state.citiesGrid.isLoading = false;
      })
      .addCase(getCitiesList.rejected, (state, action) => {
        state.citiesGrid.status = "failed";
        state.citiesGrid.isLoading = false;
        if (action.payload) {
          state.citiesGrid.error = action.payload;
          state.citiesGrid.message = action.payload;
        } else {
          state.citiesGrid.message = "An unexpected error occurred";
        }
      }) 
      .addCase(deleteCity.pending, (state) => {
          state.citiesGrid.status = "loading";
        })
        .addCase(deleteCity.fulfilled, (state, action) => {
          state.citiesGrid.status = "idle";
          state.citiesGrid.data = action.payload;
          state.citiesGrid.isLoading = false;
        })
        .addCase(deleteCity.rejected, (state, action) => {
          state.citiesGrid.status = "failed";
          state.citiesGrid.isLoading = false;
          if (action.payload) {
            state.citiesGrid.error = action.payload;
            state.citiesGrid.message = action.payload;
          } else {
            state.citiesGrid.message = "An unexpected error occurred";
          }
        });
    },
  });

export default cityReducer.reducer;
export const { clearBlobData } = cityReducer.actions;